import { isApolloError } from '@apollo/client/errors';
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  ErrorEvent,
  EventHint,
  SeverityLevel,
} from '@sentry/nextjs';

type Breadcrumb = {
  level: SeverityLevel;
  message: string;
  category?: string;
};

export const captureBreadcrumb = (breadcrumb: Breadcrumb) => {
  addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level,
  });
};

export const captureError = captureException;

export const captureErrorMessage = captureMessage;

// An ApolloError's stack only has Apollo frames, so Sentry groups every failed
// operation into one issue. The Apollo error link records which operation each
// GraphQL/network error came from, and tagApolloError puts it on the event.
const operationNames = new WeakMap<object, string>();

export const rememberApolloOperation = (
  error: object,
  operationName: string,
) => {
  operationNames.set(error, operationName);
};

export const tagApolloError = (event: ErrorEvent, hint: EventHint) => {
  const error = hint.originalException;
  if (!(error instanceof Error) || !isApolloError(error)) return event;

  const source = [error.networkError, ...error.graphQLErrors].find(
    (err) => err && operationNames.has(err),
  );
  const operationName = source ? operationNames.get(source) : undefined;
  if (!operationName) return event;

  return {
    ...event,
    tags: { ...event.tags, 'graphql.operation': operationName },
    fingerprint: ['ApolloError', operationName],
  };
};

// A script (an extension, a third-party tag) can dispatch a fake DOM event such
// as `new CustomEvent('unhandledrejection')`, which Sentry reports with no
// stack. Real rejections are trusted and Sentry reports their reason, not the
// event, so an untrusted event in originalException is never ours.
export const beforeSend = (event: ErrorEvent, hint: EventHint) => {
  const error = hint.originalException;
  if (error instanceof Event && !error.isTrusted) return null;

  return tagApolloError(event, hint);
};

// GTM queues dataLayer events until gtm.js loads, then sends one GA4 hit per
// event at once, so page loads show a burst of identical /g/collect requests
// that Sentry flags as an N+1 API call. Analytics beacons say nothing about
// our app's performance, so don't create spans for them.
const analyticsHosts = new Set([
  'gtm.svc.avantarte.com',
  'api-eu.mixpanel.com',
  'www.google.com',
  'ad.doubleclick.net',
]);

export const shouldCreateSpanForRequest = (url: string) => {
  try {
    return !analyticsHosts.has(new URL(url, 'https://avantarte.com').hostname);
  } catch {
    return true;
  }
};
